import type { GlobalStatusSnapshot } from '@/mocks/saSystemHealthCenter';

interface Props {
  globalStatus: GlobalStatusSnapshot;
}

export default function GlobalStatusBar({ globalStatus }: Props) {
  const statusConfig: Record<string, { bg: string; text: string; dot: string; icon: string }> = {
    healthy: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: 'ri-check-double-line' },
    warning: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', icon: 'ri-error-warning-line' },
    critical: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', dot: 'bg-red-500', icon: 'ri-close-circle-line' },
  };

  const config = statusConfig[globalStatus.overallStatus];

  return (
    <div className={`${config.bg} border rounded-2xl p-5`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl bg-white flex items-center justify-center`}>
            <i className={`${config.icon} ${config.text} text-xl`}></i>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${config.dot} ${globalStatus.overallStatus !== 'healthy' ? 'animate-pulse' : ''}`}></span>
              <p className={`text-sm font-bold ${config.text}`}>
                {globalStatus.overallStatus === 'healthy'
                  ? 'Todos los sistemas operativos'
                  : globalStatus.overallStatus === 'warning'
                    ? 'Atención requerida'
                    : 'Incidente crítico'}
              </p>
            </div>
            <p className="text-foreground-500 text-[11px] mt-0.5">{globalStatus.overallLabel}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <StatusBadge color="emerald" count={globalStatus.online} label="Operativos" />
          <StatusBadge color="amber" count={globalStatus.degraded} label="Degradados" />
          <StatusBadge color="sky" count={globalStatus.maintenance} label="Mantenimiento" />
          <StatusBadge color="red" count={globalStatus.offline} label="Caídos" />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ color, count, label }: { color: string; count: number; label: string }) {
  const colorMap: Record<string, { bg: string; text: string; show: boolean }> = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', show: true },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', show: count > 0 },
    sky: { bg: 'bg-sky-50', text: 'text-sky-700', show: count > 0 },
    red: { bg: 'bg-red-50', text: 'text-red-700', show: count > 0 },
  };

  const cm = colorMap[color];
  if (!cm.show) return null;

  return (
    <div className={`${cm.bg} rounded-xl px-3 py-2 text-center min-w-[60px]`}>
      <p className={`text-lg font-bold ${cm.text}`}>{count}</p>
      <p className={`text-[9px] font-medium ${cm.text}`}>{label}</p>
    </div>
  );
}